/**
 * 3D models — GLB, glTF and OBJ.
 *
 * A BIM export or a visualisation model is the most accurate description of a
 * mall anyone is likely to hand over, and the least directly usable: it is a
 * soup of triangles with no notion of a tenancy. Extracting rooms from it by
 * chasing mesh topology is a research problem, because real exports are
 * non-manifold, double-sided, full of duplicated geometry, and frequently have
 * walls that do not quite meet.
 *
 * So this module does the boring, robust thing instead: it takes a HORIZONTAL
 * SECTION through the model at about waist height, the same cut an architect
 * would draw, and rasterises the resulting line segments into an image. That
 * image is a floor plan, and it goes through the exact pipeline a scanned PNG
 * does — flood the space between the walls, trace each room, classify it.
 *
 * The section is forgiving in a way topology-chasing is not: a wall that is a
 * millimetre short still draws a line, and a doorway still shows as a gap.
 * The one thing it cannot recover is colour, so a model comes back as rooms
 * with no category, and the layer/colour mapper has nothing to work with —
 * which is why the cut height is exposed rather than assumed.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';

export const MODEL_EXTENSIONS = ['.glb', '.gltf', '.obj'];

export const MODEL_DEFAULTS = {
    /** Height of the cut above the model's floor, in model units. */
    cutHeight: 1.2,
    /** Raster width for the produced section. */
    rasterWidth: 1800,
    /** Line weight of the cut, in pixels. */
    lineWidth: 2.5,
    /** Ignore segments shorter than this fraction of the model's span. */
    minSegmentFraction: 0.0015,
};

export function isModelFile(file) {
    const n = (file.name || '').toLowerCase();
    return MODEL_EXTENSIONS.some((ext) => n.endsWith(ext));
}

/** Parse a model file into a THREE.Object3D. */
export async function loadModel(file) {
    const name = (file.name || '').toLowerCase();

    if (name.endsWith('.obj')) {
        const text = await file.text();
        return new OBJLoader().parse(text);
    }

    const buffer = await file.arrayBuffer();
    return new Promise((resolve, reject) => {
        new GLTFLoader().parse(
            buffer,
            '',
            (gltf) => resolve(gltf.scene || gltf.scenes[0]),
            () => reject(new Error('That model could not be read. GLB, glTF and OBJ are supported.')),
        );
    });
}

/**
 * Collect every triangle edge crossing a horizontal plane.
 *
 * Works on world-space positions so the model's own transforms are respected,
 * and reads indexed and non-indexed geometry alike, because exporters produce
 * both and neither is unusual.
 */
function sectionSegments(root, planeY) {
    const segments = [];
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const c = new THREE.Vector3();

    root.updateWorldMatrix(true, true);

    root.traverse((node) => {
        if (!node.isMesh || !node.geometry) return;
        const geo = node.geometry;
        const pos = geo.attributes && geo.attributes.position;
        if (!pos) return;
        const index = geo.index;
        const m = node.matrixWorld;
        const count = index ? index.count : pos.count;

        for (let i = 0; i + 2 < count; i += 3) {
            const i0 = index ? index.getX(i) : i;
            const i1 = index ? index.getX(i + 1) : i + 1;
            const i2 = index ? index.getX(i + 2) : i + 2;

            a.fromBufferAttribute(pos, i0).applyMatrix4(m);
            b.fromBufferAttribute(pos, i1).applyMatrix4(m);
            c.fromBufferAttribute(pos, i2).applyMatrix4(m);

            const hits = [];
            const cross = (p, q) => {
                const dp = p.y - planeY;
                const dq = q.y - planeY;
                if ((dp > 0) === (dq > 0)) return;
                const t = dp / (dp - dq);
                hits.push([p.x + ((q.x - p.x) * t), p.z + ((q.z - p.z) * t)]);
            };
            cross(a, b);
            cross(b, c);
            cross(c, a);

            // A triangle crossing a plane produces exactly two intersections;
            // anything else is a degenerate or coplanar face, which contributes
            // no useful line.
            if (hits.length === 2) segments.push(hits);
        }
    });

    return segments;
}

/**
 * Cut a model and draw the section as a floor plan.
 *
 * @returns {{ canvas, width, height, spanX, spanZ, segments, bbox }}
 *   `spanX`/`spanZ` are the model's real extent, so the importer can offer the
 *   scale rather than making the operator measure a line on a drawing.
 */
export function sectionToPlan(root, options = {}) {
    const opts = { ...MODEL_DEFAULTS, ...options };

    const box = new THREE.Box3().setFromObject(root);
    if (box.isEmpty()) throw new Error('That model appears to be empty.');

    const size = box.getSize(new THREE.Vector3());
    const planeY = box.min.y + Math.min(opts.cutHeight, Math.max(size.y * 0.05, size.y * 0.5));

    const segments = sectionSegments(root, planeY);
    if (!segments.length) {
        throw new Error('Nothing was cut at that height. Try a different section height.');
    }

    // Frame on what the CUT actually produced rather than the whole model, so a
    // roof canopy or a surrounding site mesh does not shrink the plan.
    let minX = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxZ = -Infinity;
    segments.forEach(([p, q]) => {
        [p, q].forEach(([x, z]) => {
            if (x < minX) minX = x;
            if (z < minZ) minZ = z;
            if (x > maxX) maxX = x;
            if (z > maxZ) maxZ = z;
        });
    });

    const spanX = maxX - minX;
    const spanZ = maxZ - minZ;
    const span = Math.max(spanX, spanZ);
    if (!(span > 0)) throw new Error('The section came out degenerate.');

    const pad = span * 0.02;
    const width = opts.rasterWidth;
    const height = Math.max(64, Math.round((width * (spanZ + (2 * pad))) / (spanX + (2 * pad))));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fcfcfd';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#141418';
    ctx.lineWidth = opts.lineWidth;
    ctx.lineCap = 'round';

    const sx = width / (spanX + (2 * pad));
    const sz = height / (spanZ + (2 * pad));
    const minLen = opts.minSegmentFraction * span;

    ctx.beginPath();
    segments.forEach(([p, q]) => {
        if (Math.hypot(q[0] - p[0], q[1] - p[1]) < minLen) return;
        ctx.moveTo((p[0] - minX + pad) * sx, (p[1] - minZ + pad) * sz);
        ctx.lineTo((q[0] - minX + pad) * sx, (q[1] - minZ + pad) * sz);
    });
    ctx.stroke();

    return {
        canvas,
        width,
        height,
        segments: segments.length,
        cutY: planeY,
        // Real-world extent of the sheet, including the padding, so the plan's
        // scale is known outright and calibration becomes a confirmation.
        spanX: spanX + (2 * pad),
        spanZ: spanZ + (2 * pad),
        modelSize: { x: size.x, y: size.y, z: size.z },
    };
}
